import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Clock, Flame, Loader2, MapPin, MessageSquare, Send, ShieldAlert, StopCircle } from 'lucide-react';
import { Card, Button } from '../components';
import { chatAPI, type Message } from '../services/chatService';
import { useAuthStore } from '../store/authStore';
import {
  accepteerWaakvlamVoorwaarden,
  checkIn,
  checkOut,
  duurTekst,
  euro,
  fetchVpOverzicht,
  huidigePositie,
  pingRonde,
  trekWaakvlamAkkoordIn,
  type VpOverzicht,
} from '../services/vertrouwenspersoonApi';

const QUERY_KEY = ['vertrouwenspersoon', 'professional'];
const foutTekst = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

const PERIODE_STATUS: Record<string, string> = {
  GEINCASSEERD: 'Geïncasseerd — uitbetaling na afloop van de maand',
  UITBETAALD: 'Uitbetaald',
  UITBETALING_MISLUKT: 'Uitbetaling wacht op je Stripe-koppeling',
  SLA_BREACH_TERUGGESTORT: 'SLA_BREACH — fee teruggestort aan opdrachtgever',
  SLA_BREACH_TERUGSTORTING_MISLUKT: 'SLA_BREACH — terugstorting in behandeling',
};

function useNu(intervalMs = 1000) {
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNu(Date.now()), intervalMs);
    return () => clearInterval(iv);
  }, [intervalMs]);
  return nu;
}

function klok(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
}

/** Mijn Belagom → Waakvlam & Zichtbaarheid (alleen relevant voor vertrouwenspersonen). */
export const ProfessionalWaakvlam: React.FC = () => {
  const { data, isLoading, error } = useQuery({ queryKey: QUERY_KEY, queryFn: fetchVpOverzicht, refetchInterval: 30_000 });
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden…
      </div>
    );
  }
  if (error || !data) return <Card className="text-red-700">Deze pagina kon niet worden geladen.</Card>;
  return (
    <div className="space-y-8 max-w-3xl">
      <ZichtbaarheidRonde data={data.zichtbaarheid} />
      <Waakvlam data={data.waakvlam} />
    </div>
  );
};

// ─── Zichtbaarheidsronde (GPS-Grendel) ───────────────────────────────────────

const ZichtbaarheidRonde: React.FC<{ data: VpOverzicht['zichtbaarheid'] }> = ({ data }) => {
  const queryClient = useQueryClient();
  const nu = useNu();
  const [blokkade, setBlokkade] = useState<string | null>(null);
  const [opLocatie, setOpLocatie] = useState<boolean | null>(null);
  const ronde = data.actieve_ronde;
  const ververs = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  // Positie periodiek bevestigen zolang de ronde loopt en deze pagina open is.
  // Afhankelijk van het id, niet van het object: elke refetch levert een nieuw object op.
  const rondeId = ronde?.id;
  useEffect(() => {
    if (!rondeId) return;
    let actief = true;
    const bevestig = async () => {
      try {
        const r = await pingRonde(rondeId, await huidigePositie());
        if (actief) setOpLocatie(r.op_locatie);
      } catch {
        if (actief) setOpLocatie(false);
      }
    };
    void bevestig();
    const iv = setInterval(bevestig, data.ping_interval_s * 1000);
    return () => {
      actief = false;
      clearInterval(iv);
    };
  }, [rondeId, data.ping_interval_s]);

  const start = useMutation({
    mutationFn: async (mandaatId: string) => checkIn(mandaatId, await huidigePositie()),
    onSuccess: () => {
      setBlokkade(null);
      toast.success('Aanwezig op locatie — de urentimer loopt');
      ververs();
    },
    onError: (e) => setBlokkade(foutTekst(e, 'Inchecken mislukt')),
  });

  const stop = useMutation({
    mutationFn: async () => {
      let positie = null;
      try {
        positie = await huidigePositie();
      } catch {
        if (!window.confirm('Uw locatie kan niet bepaald worden. Afsluiten op de laatst bevestigde positie?')) throw new Error('Afgebroken');
      }
      return checkOut(ronde!.id, positie);
    },
    onSuccess: (r) => {
      toast.success(
        `Ronde beëindigd: ${duurTekst(r.gefactureerde_ms)} (${euro(r.bedrag)})` +
          (r.eind_bron === 'LAATSTE_PING' ? ' — afgesloten op uw laatst bevestigde positie op locatie' : '') +
          (r.plafond_bereikt ? ' — begrensd op het maandplafond' : ''),
        { duration: 9000 }
      );
      setOpLocatie(null);
      ververs();
    },
    onError: (e) => {
      if ((e as Error)?.message !== 'Afgebroken') toast.error(foutTekst(e, 'Beëindigen mislukt'));
    },
  });

  if (data.mandaten.length === 0 && !ronde) {
    return (
      <section>
        <Kop icon={MapPin} titel="Fysieke zichtbaarheidsronde" />
        <Card className="text-sm text-gray-600">
          Nog geen opdrachtgever heeft een zichtbaarheidsmandaat voor u vastgelegd. Zodra dat gebeurt, kunt u hier op locatie inchecken.
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <Kop icon={MapPin} titel="Fysieke zichtbaarheidsronde" />
      <p className="text-sm text-gray-600">
        Inchecken kan alleen binnen {data.radius_m} meter van het vestigingsadres, met een GPS-fix van ± {data.max_nauwkeurigheid_m} m of beter. Houd deze
        pagina open tijdens de ronde: uw positie wordt elke {Math.round(data.ping_interval_s / 60)} minuten bevestigd. Checkt u buiten de straal uit, dan telt de
        tijd tot de laatst bevestigde positie op locatie.
      </p>

      {ronde ? (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 text-center">
          <p className="text-sm font-semibold text-emerald-800">Aanwezig op locatie</p>
          <p className="text-4xl font-mono font-semibold text-navy my-2 tabular-nums">{klok(nu - new Date(ronde.check_in_at).getTime())}</p>
          <p className="text-xs text-gray-600">
            {ronde.referentie_code} · laatst bevestigd {new Date(ronde.laatst_op_locatie_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
          </p>
          {opLocatie === false && (
            <p className="mt-2 text-xs font-medium text-red-700">Uw huidige positie ligt buiten de straal — deze tijd telt niet mee.</p>
          )}
          <button
            type="button"
            onClick={() => stop.mutate()}
            disabled={stop.isPending}
            className="mt-4 w-full rounded-xl bg-red-600 px-4 py-4 text-base font-semibold text-white shadow hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {stop.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <StopCircle className="w-5 h-5" />} Zichtbaarheidsronde Beëindigen
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {blokkade && (
            <div className="flex items-start gap-2 rounded-xl border-2 border-red-300 bg-red-50 p-4 text-sm font-medium text-red-800">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" /> {blokkade}
            </div>
          )}
          {data.mandaten.map((m) => (
            <Card key={m.id}>
              <p className="text-sm font-semibold text-gray-900">
                {m.opdrachtgever} · {m.vestiging.naam}
              </p>
              <p className="text-xs text-gray-500">{m.vestiging.bag_adres ?? [m.vestiging.adres, m.vestiging.postcode, m.vestiging.plaats].filter(Boolean).join(', ')}</p>
              <p className="text-xs text-gray-600 mt-1">
                Nog {duurTekst(m.resterend_ms)} van {duurTekst(m.max_minuten_per_maand * 60_000)} deze maand · {euro(m.uurtarief)}/u
              </p>
              <button
                type="button"
                onClick={() => start.mutate(m.id)}
                disabled={start.isPending || m.resterend_ms <= 0}
                className="mt-3 w-full rounded-xl bg-gold px-4 py-4 text-base font-semibold text-navy shadow hover:brightness-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {start.isPending && start.variables === m.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />} Start Fysieke
                Zichtbaarheidsronde
              </button>
            </Card>
          ))}
        </div>
      )}

      {data.recente_rondes.length > 0 && (
        <Card padding={false}>
          <p className="px-4 pt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Recente rondes</p>
          <ul className="divide-y divide-gray-100">
            {data.recente_rondes.map((r) => (
              <li key={r.id} className="px-4 py-2 text-sm flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {new Date(r.check_in_at).toLocaleDateString('nl-NL')} · {r.vestiging}
                  {r.eind_bron === 'LAATSTE_PING' && <span className="text-xs text-amber-700"> · tot laatste positie</span>}
                </span>
                <span className="text-gray-700 whitespace-nowrap">
                  {duurTekst(r.gefactureerde_ms)} · {euro(r.bedrag)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
};

// ─── Waakvlam ─────────────────────────────────────────────────────────────────

const Waakvlam: React.FC<{ data: VpOverzicht['waakvlam'] }> = ({ data }) => {
  const queryClient = useQueryClient();
  const nu = useNu(30_000);
  const [gesprek, setGesprek] = useState<string | null>(null);
  const ververs = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const akkoord = useMutation({
    mutationFn: () => accepteerWaakvlamVoorwaarden(data.voorwaarden.hash),
    onSuccess: () => {
      toast.success('Voorwaarden geaccepteerd — opdrachtgevers kunnen u nu als Waakvlam koppelen');
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, 'Accepteren mislukt')),
  });
  const intrekken = useMutation({
    mutationFn: trekWaakvlamAkkoordIn,
    onSuccess: () => {
      toast.success('Akkoord ingetrokken');
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, 'Intrekken mislukt')),
  });

  if (gesprek) return <HulpoproepChat conversationId={gesprek} onTerug={() => { setGesprek(null); ververs(); }} />;

  return (
    <section className="space-y-3">
      <Kop icon={Flame} titel="Waakvlam — paraatheid" />
      <p className="text-sm text-gray-600">
        Met een Waakvlam ontvangt u een vast maandelijks bedrag voor uw paraatheid. Daar staat een harde reactieplicht tegenover: op iedere hulpoproep
        reageert u binnen {data.sla_uren} uur via de chat. Mist u die termijn, dan springt de status op SLA_BREACH en vervalt de fee van die maand.
      </p>

      {!data.voorwaarden.geaccepteerd_op ? (
        <Card>
          <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 font-sans">
            {data.voorwaarden.tekst}
          </pre>
          <Button className="mt-3" loading={akkoord.isPending} onClick={() => akkoord.mutate()}>
            Ik accepteer de Waakvlam-voorwaarden
          </Button>
        </Card>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
          <span>Voorwaarden v{data.voorwaarden.versie} geaccepteerd op {new Date(data.voorwaarden.geaccepteerd_op).toLocaleDateString('nl-NL')}.</span>
          {data.abonnementen.length === 0 && (
            <button type="button" className="underline" onClick={() => intrekken.mutate()}>
              Akkoord intrekken
            </button>
          )}
        </div>
      )}

      {data.abonnementen.map((a) => (
        <Card key={a.id}>
          <p className="text-sm font-semibold text-gray-900">{a.opdrachtgever}</p>
          <p className="text-xs text-gray-600">
            {a.status === 'ACTIVE' || a.status === 'PAST_DUE' ? 'Waakvlam brandt' : 'Wacht op SEPA-machtiging opdrachtgever'} · uw aandeel{' '}
            {euro(a.vp_per_maand)} per maand{a.opgezegd_op ? ' · opgezegd per einde maand' : ''}
          </p>
        </Card>
      ))}

      <h3 className="text-sm font-semibold text-gray-900 pt-2">Hulpoproepen</h3>
      {data.gesprekken.length === 0 ? (
        <Card className="text-sm text-gray-500">Geen hulpoproepen.</Card>
      ) : (
        <ul className="space-y-2">
          {data.gesprekken.map((g) => {
            const rest = g.open_timer ? new Date(g.open_timer.deadline).getTime() - nu : null;
            return (
              <li key={g.conversation_id}>
                <button
                  type="button"
                  onClick={() => setGesprek(g.conversation_id)}
                  className={`w-full flex items-center justify-between gap-3 rounded-xl border p-3 text-left bg-white hover:border-gray-300 ${
                    rest != null ? (rest < 4 * 3600_000 ? 'border-red-300' : 'border-amber-300') : 'border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <MessageSquare className="w-4 h-4 text-navy flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">{g.meldingscode}</span>
                  </span>
                  {rest != null ? (
                    <span className={`flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${rest < 4 * 3600_000 ? 'text-red-700' : 'text-amber-700'}`}>
                      <Clock className="w-3.5 h-3.5" /> {rest > 0 ? `nog ${duurTekst(rest)} om te reageren` : 'termijn verstreken'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">beantwoord</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {data.perioden.length > 0 && (
        <Card padding={false}>
          <p className="px-4 pt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Paraatheidsvergoeding</p>
          <ul className="divide-y divide-gray-100">
            {data.perioden.map((p) => (
              <li key={`${p.periode}-${p.bedrijfsnaam}`} className="px-4 py-2 text-sm flex justify-between gap-3">
                <span>
                  {p.periode} · {p.bedrijfsnaam ?? 'Opdrachtgever'}
                  <span className="block text-xs text-gray-500">{PERIODE_STATUS[p.status] ?? p.status}</span>
                </span>
                <span className={p.status.startsWith('SLA_BREACH') ? 'line-through text-gray-400' : 'text-gray-900'}>{euro(p.vp_aandeel)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
};

const HulpoproepChat: React.FC<{ conversationId: string; onTerug: () => void }> = ({ conversationId, onTerug }) => {
  const { user } = useAuthStore();
  const [berichten, setBerichten] = useState<Message[]>([]);
  const [invoer, setInvoer] = useState('');
  const [bezig, setBezig] = useState(false);
  const eindRef = useRef<HTMLDivElement>(null);

  const laad = async () => {
    try {
      setBerichten(await chatAPI.getMessages(conversationId));
      setTimeout(() => eindRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) {
      console.error('Berichten laden mislukt', e);
    }
  };
  useEffect(() => {
    void laad();
    const iv = setInterval(laad, 5000);
    return () => clearInterval(iv);
    // laad gebruikt alleen setters en refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const verstuur = async () => {
    if (!invoer.trim() || bezig) return;
    setBezig(true);
    try {
      await chatAPI.sendMessage(conversationId, invoer.trim());
      setInvoer('');
      await laad();
    } catch (e) {
      toast.error(foutTekst(e, 'Versturen mislukt'));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 14rem)' }}>
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <button type="button" onClick={onTerug} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Terug">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p className="text-sm font-semibold text-gray-900">Hulpoproep</p>
        <p className="text-xs text-gray-500 ml-auto">Berichten worden na 24 uur gewist</p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {berichten.map((m) => {
          const eigen = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${eigen ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words ${eigen ? 'bg-navy text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                {m.content}
                <span className={`block mt-1 text-[10px] ${eigen ? 'text-blue-100' : 'text-gray-400'}`}>
                  {new Date(m.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={eindRef} />
      </div>
      <div className="p-2 border-t border-gray-100 flex gap-2">
        <input
          value={invoer}
          onChange={(e) => setInvoer(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && verstuur()}
          placeholder="Typ uw reactie…"
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"
        />
        <button type="button" onClick={verstuur} disabled={!invoer.trim() || bezig} className="p-2 rounded-lg bg-navy text-white disabled:opacity-40" aria-label="Verstuur">
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const Kop: React.FC<{ icon: React.ComponentType<{ className?: string }>; titel: string }> = ({ icon: Icon, titel }) => (
  <h2 className="flex items-center gap-2 text-lg font-semibold text-navy">
    <Icon className="w-5 h-5 text-gold" /> {titel}
  </h2>
);

export default ProfessionalWaakvlam;
